/**
 * Wardrobe item library — small SVG components that overlay on top of
 * MascotBody. Each slot has 5 picks. Female agents (Sarah, Eva) get
 * gendered variants where it matters (jumper cut, watch style, chain).
 *
 * Each item is a React component that receives { palette } so colours
 * stay tied to the agent's signature trim.
 *
 * COORDINATES — match MascotBody's 240×320 viewBox. Head is at y≈54-166,
 * torso y≈200-280, arms reach to y≈100.
 */
import React from "react";

const cls = (palette, slot) => palette.trim;

// ─── HATS ─────────────────────────────────────────────────────────
export const HATS = {
  none: { label: "None", render: () => null },
  bobble: {
    label: "Bobble Hat",
    render: ({ palette }) => (
      <g>
        <path d="M 62 78 Q 120 18 178 78 L 178 90 L 62 90 Z" fill={palette.trim}/>
        <rect x="62" y="86" width="116" height="10" rx="4" fill={palette.jumper}/>
        <circle cx="120" cy="18" r="10" fill="#fafafa"/>
      </g>
    ),
  },
  cap: {
    label: "Baseball Cap",
    render: ({ palette }) => (
      <g>
        <path d="M 65 88 Q 120 56 175 88 L 175 80 Q 120 52 65 80 Z" fill={palette.jumper}/>
        <rect x="60" y="86" width="135" height="6" rx="3" fill="#1a1a24"/>
        <circle cx="120" cy="68" r="3" fill={palette.trim}/>
      </g>
    ),
  },
  beanie: {
    label: "Beanie",
    render: ({ palette }) => (
      <g>
        <path d="M 65 90 Q 70 50 120 50 Q 170 50 175 90 L 175 96 L 65 96 Z" fill={palette.jumper}/>
        <rect x="62" y="92" width="116" height="10" rx="5" fill={palette.trim}/>
        <path d="M 95 70 L 95 90 M 120 60 L 120 90 M 145 70 L 145 90" stroke={palette.trim} strokeWidth="1.5" opacity="0.4"/>
      </g>
    ),
  },
  tophat: {
    label: "Top Hat",
    render: ({ palette }) => (
      <g>
        <rect x="85" y="20" width="70" height="62" fill="#0e0e16" stroke={palette.trim} strokeWidth="2"/>
        <rect x="65" y="78" width="110" height="10" rx="3" fill="#0e0e16"/>
        <rect x="85" y="62" width="70" height="6" fill={palette.trim}/>
      </g>
    ),
  },
  crown: {
    label: "Crown",
    render: ({ palette }) => (
      <g>
        <path d="M 70 80 L 80 50 L 95 70 L 120 40 L 145 70 L 160 50 L 170 80 Z"
              fill="#FCD34D" stroke="#D97706" strokeWidth="1.5"/>
        <circle cx="80" cy="50" r="4" fill="#EF4444"/>
        <circle cx="120" cy="40" r="5" fill="#10B981"/>
        <circle cx="160" cy="50" r="4" fill="#3B82F6"/>
      </g>
    ),
  },
  afro: {
    label: "Afro",
    render: () => (
      <g>
        <circle cx="120" cy="62" r="42" fill="#1c1917"/>
        <circle cx="90"  cy="58" r="20" fill="#1c1917"/>
        <circle cx="150" cy="58" r="20" fill="#1c1917"/>
        <circle cx="105" cy="42" r="14" fill="#1c1917"/>
        <circle cx="135" cy="42" r="14" fill="#1c1917"/>
      </g>
    ),
  },
  sunglasses: {
    label: "Sunglasses",
    render: ({ palette }) => (
      <g>
        <ellipse cx="98"  cy="108" rx="16" ry="11" fill="#0a0a14" stroke={palette.trim} strokeWidth="1.5"/>
        <ellipse cx="142" cy="108" rx="16" ry="11" fill="#0a0a14" stroke={palette.trim} strokeWidth="1.5"/>
        <line x1="114" y1="108" x2="126" y2="108" stroke={palette.trim} strokeWidth="2"/>
        <line x1="82"  y1="108" x2="74"  y2="105" stroke={palette.trim} strokeWidth="2"/>
        <line x1="158" y1="108" x2="166" y2="105" stroke={palette.trim} strokeWidth="2"/>
      </g>
    ),
  },
};

// ─── JUMPERS ───────────────────────────────────────────────────────
// Each renders the torso colour override + a pattern overlay
export const JUMPERS = {
  plain: { label: "Plain", render: () => null },
  striped: {
    label: "Striped",
    render: ({ palette }) => (
      <g>
        <rect x="62" y="218" width="116" height="6" fill={palette.trim} opacity="0.85"/>
        <rect x="62" y="240" width="116" height="6" fill={palette.trim} opacity="0.85"/>
        <rect x="62" y="262" width="116" height="6" fill={palette.trim} opacity="0.85"/>
      </g>
    ),
  },
  hoodie: {
    label: "Hoodie",
    render: ({ palette }) => (
      <g>
        <path d="M 70 200 Q 120 160 170 200" fill={palette.jumper} stroke={palette.trim} strokeWidth="2"/>
        <path d="M 115 200 L 115 280 M 125 200 L 125 280" stroke={palette.trim} strokeWidth="1.5" opacity="0.6"/>
      </g>
    ),
  },
  polo: {
    label: "Polo",
    render: ({ palette }) => (
      <g>
        <path d="M 92 212 L 116 232 L 120 200 L 124 232 L 148 212"
              fill="none" stroke={palette.trim} strokeWidth="2"/>
        <circle cx="120" cy="244" r="2.5" fill={palette.trim}/>
        <circle cx="120" cy="258" r="2.5" fill={palette.trim}/>
      </g>
    ),
  },
  knit: {
    label: "Knit Pattern",
    render: ({ palette }) => (
      <g opacity="0.7">
        {[0,1,2,3,4,5].map(i => (
          <path key={i}
                d={`M ${70 + i * 18} 220 L ${79 + i * 18} 228 L ${88 + i * 18} 220`}
                fill="none" stroke={palette.trim} strokeWidth="1.5"/>
        ))}
        {[0,1,2,3,4,5].map(i => (
          <path key={`b-${i}`}
                d={`M ${70 + i * 18} 250 L ${79 + i * 18} 258 L ${88 + i * 18} 250`}
                fill="none" stroke={palette.trim} strokeWidth="1.5"/>
        ))}
      </g>
    ),
  },
  crop: {  // ladies' option
    label: "Cropped (♀)",
    render: ({ palette }) => (
      <g>
        <rect x="62" y="248" width="116" height="4" fill={palette.trim}/>
        <path d="M 70 248 L 175 248" stroke="#0a0a14" strokeWidth="2"/>
      </g>
    ),
  },
};

// ─── JACKETS (on TOP of jumper) ────────────────────────────────────
export const JACKETS = {
  none: { label: "None", render: () => null },
  bomber: {
    label: "Bomber",
    render: ({ palette }) => (
      <g>
        <path d="M 55 200 L 65 200 L 65 280 L 50 280 Z" fill="#262626" stroke={palette.trim} strokeWidth="1.5"/>
        <path d="M 175 200 L 185 200 L 190 280 L 175 280 Z" fill="#262626" stroke={palette.trim} strokeWidth="1.5"/>
        <rect x="50" y="272" width="142" height="8" rx="3" fill="#0a0a14"/>
        <rect x="50" y="200" width="142" height="6" fill="#0a0a14"/>
      </g>
    ),
  },
  leather: {
    label: "Leather",
    render: () => (
      <g>
        <path d="M 60 200 L 75 200 L 75 280 L 55 280 Z" fill="#1c1917" stroke="#52525b" strokeWidth="1.5"/>
        <path d="M 165 200 L 180 200 L 185 280 L 165 280 Z" fill="#1c1917" stroke="#52525b" strokeWidth="1.5"/>
        <circle cx="68" cy="220" r="2" fill="#a3a3a3"/>
        <circle cx="68" cy="240" r="2" fill="#a3a3a3"/>
        <circle cx="68" cy="260" r="2" fill="#a3a3a3"/>
      </g>
    ),
  },
  denim: {
    label: "Denim",
    render: () => (
      <g>
        <path d="M 58 200 L 72 200 L 72 280 L 53 280 Z" fill="#1e40af" stroke="#3b82f6" strokeWidth="1.5"/>
        <path d="M 168 200 L 182 200 L 187 280 L 168 280 Z" fill="#1e40af" stroke="#3b82f6" strokeWidth="1.5"/>
        <path d="M 70 215 L 70 275" stroke="#fbbf24" strokeWidth="1" strokeDasharray="3 2"/>
      </g>
    ),
  },
  blazer: {
    label: "Blazer",
    render: () => (
      <g>
        <path d="M 60 200 L 100 200 L 120 260 L 60 280 Z" fill="#171717" stroke="#71717a" strokeWidth="1.5"/>
        <path d="M 180 200 L 140 200 L 120 260 L 180 280 Z" fill="#171717" stroke="#71717a" strokeWidth="1.5"/>
        <path d="M 100 200 L 110 224 M 140 200 L 130 224" stroke="#d4d4d8" strokeWidth="1.2"/>
      </g>
    ),
  },
  puffer: {
    label: "Puffer (warm)",
    render: ({ palette }) => (
      <g>
        <path d="M 55 200 L 75 200 L 75 280 L 50 280 Z" fill={palette.jumper} stroke="#fff" strokeWidth="0.6" opacity="0.95"/>
        <path d="M 165 200 L 185 200 L 190 280 L 165 280 Z" fill={palette.jumper} stroke="#fff" strokeWidth="0.6" opacity="0.95"/>
        {[214, 230, 246, 262].map(y => (
          <React.Fragment key={y}>
            <path d={`M 50 ${y} Q 62 ${y + 4} 75 ${y}`} fill="none" stroke="#fff" strokeWidth="1" opacity="0.5"/>
            <path d={`M 165 ${y} Q 178 ${y + 4} 190 ${y}`} fill="none" stroke="#fff" strokeWidth="1" opacity="0.5"/>
          </React.Fragment>
        ))}
      </g>
    ),
  },
};

// ─── PANTS ─────────────────────────────────────────────────────────
export const PANTS = {
  none: { label: "None", render: () => null },
  jeans: {
    label: "Jeans",
    render: () => (
      <g>
        <path d="M 80 280 L 90 320 L 115 320 L 118 280 Z" fill="#1e40af" stroke="#1e3a8a" strokeWidth="1"/>
        <path d="M 160 280 L 150 320 L 125 320 L 122 280 Z" fill="#1e40af" stroke="#1e3a8a" strokeWidth="1"/>
        <path d="M 120 280 L 120 318" stroke="#fbbf24" strokeWidth="0.6" strokeDasharray="2 2"/>
      </g>
    ),
  },
  joggers: {
    label: "Joggers",
    render: () => (
      <g>
        <path d="M 80 280 L 88 320 L 116 320 L 118 280 Z" fill="#404040"/>
        <path d="M 160 280 L 152 320 L 124 320 L 122 280 Z" fill="#404040"/>
        <line x1="100" y1="295" x2="100" y2="320" stroke="#fafafa" strokeWidth="1.5"/>
        <line x1="140" y1="295" x2="140" y2="320" stroke="#fafafa" strokeWidth="1.5"/>
      </g>
    ),
  },
  shorts: {
    label: "Shorts (summer)",
    render: ({ palette }) => (
      <g>
        <path d="M 78 280 L 90 300 L 116 300 L 118 280 Z" fill={palette.jumper} stroke={palette.trim} strokeWidth="1"/>
        <path d="M 162 280 L 150 300 L 124 300 L 122 280 Z" fill={palette.jumper} stroke={palette.trim} strokeWidth="1"/>
      </g>
    ),
  },
  smart: {
    label: "Smart Trousers",
    render: () => (
      <g>
        <path d="M 84 280 L 92 320 L 115 320 L 118 280 Z" fill="#1f2937"/>
        <path d="M 156 280 L 148 320 L 125 320 L 122 280 Z" fill="#1f2937"/>
        <path d="M 105 282 L 105 318" stroke="#52525b" strokeWidth="0.8"/>
        <path d="M 135 282 L 135 318" stroke="#52525b" strokeWidth="0.8"/>
      </g>
    ),
  },
  skirt: {
    label: "Skirt (♀)",
    render: ({ palette }) => (
      <g>
        <path d="M 70 280 L 60 320 L 180 320 L 170 280 Z" fill={palette.trim} stroke={palette.jumper} strokeWidth="1.5"/>
        <path d="M 90 280 L 86 320 M 120 280 L 120 320 M 150 280 L 154 320" stroke={palette.jumper} strokeWidth="0.8" opacity="0.6"/>
      </g>
    ),
  },
};

// ─── WATCHES ───────────────────────────────────────────────────────
export const WATCHES = {
  none: { label: "None", render: () => null },
  gold_round: {
    label: "Gold Round",
    render: () => (
      <g>
        <circle cx="52" cy="100" r="8" fill="#FCD34D" stroke="#92400E" strokeWidth="1.5"/>
        <circle cx="52" cy="100" r="5" fill="#fafafa"/>
        <line x1="52" y1="100" x2="52" y2="97" stroke="#0a0a14" strokeWidth="1"/>
        <line x1="52" y1="100" x2="55" y2="100" stroke="#0a0a14" strokeWidth="1"/>
      </g>
    ),
  },
  silver_square: {
    label: "Silver Square",
    render: () => (
      <g>
        <rect x="46" y="94" width="12" height="12" rx="2" fill="#d1d5db" stroke="#374151" strokeWidth="1"/>
        <rect x="48" y="96" width="8" height="8" fill="#0e0e16"/>
        <text x="52" y="103" textAnchor="middle" fontSize="6" fill="#34d399">12</text>
      </g>
    ),
  },
  smart: {
    label: "Smartwatch",
    render: ({ palette }) => (
      <g>
        <rect x="44" y="94" width="16" height="14" rx="3" fill="#0a0a14" stroke="#52525b" strokeWidth="1"/>
        <rect x="46" y="96" width="12" height="10" fill={palette.glow} opacity="0.85"/>
        <text x="52" y="104" textAnchor="middle" fontSize="5" fill="#0a0a14" fontWeight="bold">⌚</text>
      </g>
    ),
  },
  vintage: {
    label: "Vintage Leather",
    render: () => (
      <g>
        <rect x="44" y="92" width="16" height="4" fill="#92400E"/>
        <rect x="44" y="104" width="16" height="4" fill="#92400E"/>
        <circle cx="52" cy="100" r="7" fill="#fef3c7" stroke="#92400E" strokeWidth="1.5"/>
      </g>
    ),
  },
  sport: {
    label: "Sport (♀ rose-gold)",
    render: () => (
      <g>
        <rect x="44" y="94" width="16" height="14" rx="6" fill="#fb7185" stroke="#9f1239" strokeWidth="1"/>
        <circle cx="52" cy="101" r="5" fill="#fff1f2"/>
      </g>
    ),
  },
};

// ─── CHAINS / NECKLACES ────────────────────────────────────────────
export const CHAINS = {
  none: { label: "None", render: () => null },
  gold_thick: {
    label: "Thick Gold",
    render: () => (
      <g>
        <path d="M 88 210 Q 120 226 152 210" fill="none" stroke="#FCD34D" strokeWidth="4" strokeLinecap="round"/>
        <path d="M 88 210 Q 120 226 152 210" fill="none" stroke="#92400E" strokeWidth="1" strokeDasharray="2 2"/>
      </g>
    ),
  },
  gold_thin: {
    label: "Thin Gold",
    render: () => (
      <g>
        <path d="M 95 210 Q 120 222 145 210" fill="none" stroke="#FCD34D" strokeWidth="2"/>
        <circle cx="120" cy="222" r="3" fill="#FCD34D" stroke="#92400E" strokeWidth="0.5"/>
      </g>
    ),
  },
  silver: {
    label: "Silver",
    render: () => (
      <g>
        <path d="M 95 210 Q 120 222 145 210" fill="none" stroke="#e5e7eb" strokeWidth="2.5"/>
      </g>
    ),
  },
  pearls: {  // ladies' option
    label: "Pearls (♀)",
    render: () => (
      <g>
        {[0,1,2,3,4,5,6,7,8].map(i => (
          <circle key={i}
                  cx={92 + i * 7}
                  cy={210 + Math.sin(i * 0.4) * 6}
                  r="2.5"
                  fill="#fafafa" stroke="#a3a3a3" strokeWidth="0.5"/>
        ))}
      </g>
    ),
  },
  cross: {
    label: "Cross Pendant",
    render: () => (
      <g>
        <path d="M 95 210 Q 120 220 145 210" fill="none" stroke="#FCD34D" strokeWidth="1.5"/>
        <rect x="117" y="220" width="6"  height="14" fill="#FCD34D"/>
        <rect x="113" y="223" width="14" height="5"  fill="#FCD34D"/>
      </g>
    ),
  },
};

export const SLOTS = [
  { key: "hat",     label: "Hat / Hair",  items: HATS },
  { key: "jumper",  label: "Jumper",      items: JUMPERS },
  { key: "jacket",  label: "Jacket",      items: JACKETS },
  { key: "pants",   label: "Pants",       items: PANTS },
  { key: "watch",   label: "Watch",       items: WATCHES },
  { key: "chain",   label: "Chain",       items: CHAINS },
];

export function DEFAULT_OUTFIT() {
  return { hat: "none", jumper: "plain", jacket: "none", pants: "none", watch: "none", chain: "none" };
}

/**
 * Render the entire outfit on top of the mascot body. Used by both
 * AgentFaceStage and the wardrobe picker preview.
 */
export function OutfitOverlay({ outfit, palette }) {
  if (!outfit) outfit = DEFAULT_OUTFIT();
  return (
    <>
      {/* pants render UNDER torso/jumper, drawn first */}
      {PANTS[outfit.pants]?.render?.({ palette })}
      {/* jacket renders after pants but ON TOP of body — torso visible underneath */}
      {JACKETS[outfit.jacket]?.render?.({ palette })}
      {/* jumper pattern overlay on the torso */}
      {JUMPERS[outfit.jumper]?.render?.({ palette })}
      {/* chain over jumper/jacket */}
      {CHAINS[outfit.chain]?.render?.({ palette })}
      {/* watch is on the wrist */}
      {WATCHES[outfit.watch]?.render?.({ palette })}
      {/* hat/hair is rendered last so it sits on top of the head */}
      {HATS[outfit.hat]?.render?.({ palette })}
    </>
  );
}
